import React, { useState, useEffect, useRef, Suspense } from "react";
import { FiltersQuery } from "../../../types/FilterQuery.interface";
import { UseQueryProps } from "../../../hooks/useQuery";
import useDebounce from "../../../hooks/useDebounce";
import { FaHome } from "react-icons/fa";
import DrawStyle from "./drawStyle";
import dynamic from "next/dynamic";
import popupDetail from "../../../utils/popupDetail";

const MapGL = dynamic(() =>
  import("@urbica/react-map-gl").then((mod) => mod.default)
);

const NavigationControl = dynamic(() =>
  import("@urbica/react-map-gl").then((mod) => mod.NavigationControl)
);

const Source = dynamic(() =>
  import("@urbica/react-map-gl").then((mod) => mod.Source)
);

const Layer = dynamic(() =>
  import("@urbica/react-map-gl").then((mod) => mod.Layer)
);

const Draw = dynamic(() => import("@urbica/react-map-gl-draw"));

const Cluster: any = dynamic(() => import("@urbica/react-map-gl-cluster"));

const Marker = dynamic(() =>
  import("@urbica/react-map-gl").then((mod) => mod.Marker)
);

const ClearBoundary = dynamic(() => import("./ClearBoundary"), {
  suspense: true,
});

const ClusterMarker = dynamic(() => import("./Maker"), {
  suspense: true,
});
const Popup = dynamic(() => import("./Popup"), {
  suspense: true,
});

interface MapProps {
  mapLine: any;
  mapListing: any;
  queries: UseQueryProps;
  query: FiltersQuery;
}

const Map: React.FC<MapProps> = ({ mapLine, mapListing, queries, query }) => {
  const clusterRef = useRef<any>(undefined);
  const [viewPort, setViewPort] = useState<any>({
    latitude: mapLine.center.lat,
    longitude: mapLine.center.lng,
    zoom: mapLine.zoom,
  });


  const [popup, setPopup] = useState<any>();
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    setViewPort({
      latitude: mapLine.center.lat,
      longitude: mapLine.center.lng,
      zoom: mapLine.zoom,
    });
  }, [mapLine]);

  const onClusterClick = (cluster: any) => {
    try {
      const superCluster = clusterRef.current.getCluster();

      const zoom = superCluster.getClusterExpansionZoom(cluster.clusterId);

      setViewPort({
        ...viewPort,
        longitude: cluster.longitude,
        latitude: cluster.latitude,
        zoom,
      });
    } catch (error) {}
  };

  const _onMapMove = (e: any) => {
    const _box = e.target.getBounds();

    queries.push({
      box: [_box._ne.lng, _box._ne.lat, _box._sw.lng, _box._sw.lat],
      page: 1,
    });
  };

  const onMoveMap = useDebounce(_onMapMove, 500);

  const onDraw = async (e: any) => {
    const polygon = e.features[0].geometry.coordinates[0];

    queries.push({
      box: undefined,
      polygon: JSON.stringify(polygon),
    });
  };

  return (
    <div className="h-full">
      <MapGL
        {...(query.box &&
          query.box.length > 1 && {
            bounds: query.box,
          })}
        onMoveend={onMoveMap}
        onViewportChange={() => {}}
        boxZoom={true}
        scrollZoom={true}
        className="w-full h-full"
        mapStyle="mapbox://styles/imatteh/cl2hglt9i000z14o6u14se0pl"
        accessToken="pk.eyJ1IjoiaW1hdHRlaCIsImEiOiJja3J2dTZqamEwYTZpMnZsanUxcWhrcW9jIn0.c3dQrAz3T8LQNnfvP3z_Wg"
        {...viewPort}
      >
        {query.clear !== "true" && (
          <>
            <Source id="boundaries" type="geojson" data={mapLine.polygon} />
            <Layer
              id="boundaries"
              type="line"
              source="boundaries"
              paint={{
                "line-color": "#28666E",
                "line-width": 3,
              }}
            />
          </>
        )}
        <Cluster
          radius={50}
          extend={150}
          ref={clusterRef}
          nodeSize={36}
          component={(prop: any) => {
            return (
              <Suspense fallback={<></>}>
                <ClusterMarker
                  onClick={onClusterClick}
                  latitude={prop.latitude}
                  longitude={prop.longitude}
                  pointCount={prop.pointCount}
                />
              </Suspense>
            );
          }}
        >
          {mapListing.bboxPolygon
            ? mapListing.bboxPolygon.map((m: any, index: number) => {
                return (
                  <Marker
                    key={index}
                    latitude={m.Lat}
                    longitude={m.Lng}
                  >
                    <div
                      onClick={async () => {
                        const res = await popupDetail(m.slug);
                        setPopup(res);
                        setOpen(true);
                      }}
                      className="relative inline-block cursor-pointer select-none"
                    >
                      <span className="flex justify-center items-center gap-1 hover:scale-150 bg-green-800 hover:bg-green-600 transition-all duration-100 text-white text-center rounded-md p-2 absolute font-normal after:absolute after:top-full after:left-1/2 after:ml-[-5px] after:border-solid after:border-4 after:border-t-primary after:border-b-transparent after:border-x-transparent">
                        <FaHome />{" "}
                        {Intl.NumberFormat("en-US", {
                          notation: "compact",
                          maximumFractionDigits: 2,
                        }).format(m.ListPrice)}
                      </span>
                    </div>
                  </Marker>
                );
              })
            : mapListing.bboxList.map((m: any, index: number) => {
                return (
                  <Marker
                    key={index}
                    latitude={m.Lat}
                    longitude={m.Lng}
                  >
                    <div
                      onClick={async () => {
                        const res = await popupDetail(m.slug);
                        setPopup(res);
                        setOpen(true);
                      }}
                      className="relative inline-block cursor-pointer select-none"
                    >
                      <span className="flex justify-center items-center gap-1 hover:scale-150 bg-green-800 hover:bg-green-600 transition-all duration-100 text-white text-center rounded-md p-2 absolute font-normal after:absolute after:top-full after:left-1/2 after:ml-[-5px] after:border-solid after:border-4 after:border-t-primary after:border-b-transparent after:border-x-transparent">
                        <FaHome />{" "}
                        {Intl.NumberFormat("en-US", {
                          notation: "compact",
                          maximumFractionDigits: 2,
                        }).format(m.ListPrice)}
                      </span>
                    </div>
                  </Marker>
                );
              })}
        </Cluster>
        <Draw
          // @ts-ignore
          styles={DrawStyle()}
          pointControl={false}
          displayControlsDefault={false}
          lineStringControl={false}
          uncombineFeaturesControl={false}
          combineFeaturesControl={false}
          onDrawCreate={onDraw}
          onDrawUpdate={onDraw}
          onDrawDelete={() => {
            queries.push({
              polygon: undefined,
            });
          }}
        />
        <Suspense fallback={<></>}>
          <ClearBoundary queries={queries} query={query} />
        </Suspense>
        <NavigationControl position="top-left" />
      </MapGL>

      <Popup
        modalopen={open}
        item={popup}
        handleModalClose={() => {
          setOpen(false);
        }}
      />
    </div>
  );
};

export default Map;
